/**
 * userPreferences — client wrapper for the userPreferencesApi callable
 * (RW-2507). Postgres is the source of truth for a per-user view preference;
 * localStorage stays as a read-through cache so first paint is never blocked on
 * a round trip.
 *
 * ── Why this file has a batching loader in it ─────────────────────────
 * The preferences on one screen are read by separate hooks — TasksPanel alone
 * wants groupBy, sortDir, collapsed and the current mode's group order. One
 * callable per hook would be four cross-region round trips to draw one panel.
 * Reads are therefore queued and flushed once per tick as a single `get`, and
 * writes are debounced and flushed as a single `set`, so a drag that settles
 * several groups is one statement rather than several.
 *
 * ── Failures are reported, not swallowed ──────────────────────────────
 * This whole ticket exists because the old code wrapped every localStorage
 * write in a bare `catch {}`, which made a failed save indistinguishable from a
 * successful one. Nothing here is allowed to do that. Callers get a rejected
 * promise, and every swallowed-by-necessity failure (localStorage itself, which
 * genuinely cannot be retried) goes through reportFailure so it is at least
 * visible in a console and countable in a session replay.
 */
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase';

const MIRROR_PREFIX = 'reve_pref';

// Coalescing windows. Reads flush on the next macrotask so hooks mounting
// across a parent/child boundary in one frame still land in one request;
// writes wait longer because they arrive in bursts (a drag, a run of collapses)
// and the user is not waiting on them.
const READ_FLUSH_MS = 0;
const WRITE_DEBOUNCE_MS = 400;

/**
 * Failures we cannot hand back to a caller (localStorage quota, a disabled
 * store, a serialization fault). Routed through one function so there is a
 * single grep-able marker instead of 168 bare catch blocks.
 */
export function reportFailure(op, keys, error) {
  // eslint-disable-next-line no-console
  console.warn(`[userPreferences] ${op} failed for ${Array.isArray(keys) ? keys.join(', ') : keys}:`, error && error.message ? error.message : error);
}

// ── the real caller identity ──────────────────────────────────────────
// Super Admin "Login As" (ImpersonationContext) overrides the user OBJECT that
// components render, but deliberately leaves the auth token alone, so the
// callable writes under the super admin's own uid. The mirror must agree with
// the server about whose preferences these are, or an impersonating session
// would cache under the agent's uid and read back the super admin's row
// forever. So: the REAL token uid, never the effective user prop. Writing a
// super admin's layout into an agent's row is the RW-2428 failure, and this is
// the line that prevents it.
function currentUid() {
  try {
    return (auth && auth.currentUser && auth.currentUser.uid) || null;
  } catch (e) {
    return null;
  }
}

// ── localStorage mirror ───────────────────────────────────────────────

const mirrorKey = (uid, key) => `${MIRROR_PREFIX}:${uid}:${key}`;

// Breadcrumb: the uid the mirror was last written for.
//
// Firebase Auth rehydrates its session from IndexedDB ASYNCHRONOUSLY, so on a
// cold load of a deep link — /listings/abc, straight to the Tasks tab —
// auth.currentUser is null for the first render or two. Without this, the
// namespaced mirror would be unreadable exactly when it matters most and the
// panel would paint defaults before correcting itself: the flash this ticket
// set out to avoid. localStorage is synchronous, so one breadcrumb buys back
// the synchronous first paint.
//
// On a shared browser the breadcrumb names the PREVIOUS user, so a cold start
// can paint their view settings for one frame before the server read replaces
// them. That is what happens today — the legacy keys were never per-user — and
// it resolves as soon as auth does.
const UID_BREADCRUMB = `${MIRROR_PREFIX}:uid`;

function lastKnownUid() {
  try {
    return localStorage.getItem(UID_BREADCRUMB);
  } catch (e) {
    return null;
  }
}

/**
 * Read the mirrored value for `key`, falling back ONCE to the un-namespaced key
 * the panels wrote before this ticket.
 *
 * The legacy fallback is what stops deploy day from resetting everybody's
 * arrangements — which would be a strange way to ship "your settings stop
 * forgetting themselves". It is read-only and never rewritten: an older tab
 * still on the previous build keeps its own copy working, and the key simply
 * goes inert once every session has moved.
 *
 * Known and accepted: on a SHARED browser the legacy key has no uid in it, so a
 * second user's very first load can seed from the first user's value. That is
 * exactly what happens today — the legacy keys have never been per-user — and
 * it self-corrects on their first change. Everything written from here on is
 * namespaced, so it stops being possible going forward.
 *
 * `legacyKey` covers the case where the preference key and the old localStorage
 * key are not the same string. Only one key has needed it: the events view was
 * stored as `reve_events_view_<uid>` because localStorage is shared between the
 * users of one browser, and a row already keyed by uid should not carry the uid
 * in its name as well. Without this the rename would read as "no stored
 * preference" and quietly reset everyone who had picked Calendar.
 *
 * @returns {{ found: boolean, value: any, legacy: boolean }}
 */
export function readMirror(uid, key, { parse = true, legacyKey = null } = {}) {
  const miss = { found: false, value: undefined, legacy: false };
  try {
    // The namespaced copy needs SOME uid; the legacy one never had a uid in it,
    // so it stays readable even before auth resolves.
    const owner = uid || lastKnownUid();
    if (owner) {
      const namespaced = localStorage.getItem(mirrorKey(owner, key));
      if (namespaced !== null) {
        return { found: true, value: parse ? JSON.parse(namespaced) : namespaced, legacy: false };
      }
    }
    const legacy = localStorage.getItem(legacyKey || key);
    if (legacy !== null) {
      // Legacy values were written in two shapes by the panels: JSON for arrays
      // (`_collapsed`, `_groupOrder_*`) and a BARE string for the enums
      // (`_groupBy` was stored as `status`, not `"status"`). Try JSON first and
      // fall back to the raw string rather than dropping a value that is
      // perfectly good and just unquoted.
      try {
        return { found: true, value: parse ? JSON.parse(legacy) : legacy, legacy: true };
      } catch (e) {
        return { found: true, value: legacy, legacy: true };
      }
    }
    return miss;
  } catch (e) {
    reportFailure('mirror read', key, e);
    return miss;
  }
}

/** Mirror `value` for `key`. Best-effort by nature — the server copy is truth. */
export function writeMirror(uid, key, value) {
  const owner = uid || lastKnownUid();
  if (!owner) return;
  try {
    localStorage.setItem(mirrorKey(owner, key), JSON.stringify(value));
    if (uid) localStorage.setItem(UID_BREADCRUMB, uid);
  } catch (e) {
    reportFailure('mirror write', key, e);
  }
}

/** Drop the mirrored value for `key` (a reset-to-default). */
export function clearMirror(uid, key) {
  const owner = uid || lastKnownUid();
  if (!owner) return;
  try {
    localStorage.removeItem(mirrorKey(owner, key));
  } catch (e) {
    reportFailure('mirror clear', key, e);
  }
}

// ── in-memory cache, keyed by the uid it was filled for ───────────────

let cacheUid = null;
let cache = new Map();

/**
 * Clear the cache if the signed-in user changed. Called on every entry point
 * rather than wired to an auth subscription: it is one string compare, and it
 * cannot miss an event or leak a listener.
 */
function syncCacheOwner(uid) {
  if (cacheUid !== uid) {
    cacheUid = uid;
    cache = new Map();
  }
}

/** Test seam + sign-out hook: forget everything held in memory. */
export function resetCache() {
  cacheUid = null;
  cache = new Map();
}

/** The cached server value for `key`, or `undefined` if not loaded/known. */
export function peekCache(key) {
  syncCacheOwner(currentUid());
  return cache.get(key);
}

// ── callable ──────────────────────────────────────────────────────────

async function call(action, payload) {
  const callable = httpsCallable(functions, 'userPreferencesApi');
  const result = await callable({ action, ...(payload || {}) });
  return result && result.data;
}

// ── batched reads ─────────────────────────────────────────────────────

let readBatch = null; // { keys: Set, waiters: [{ keys, resolve, reject }], timer }

function flushReads() {
  const batch = readBatch;
  readBatch = null;
  if (!batch) return;
  const keys = [...batch.keys];

  call('get', { keys })
    .then((data) => {
      const preferences = (data && data.preferences) || {};
      const uid = currentUid();
      syncCacheOwner(uid);
      // A key the server did not return is one the user has never set. Cached
      // as null — a real "known to be absent" — so a remount does not re-ask
      // and cannot mistake it for "not loaded yet" (undefined).
      for (const key of keys) {
        cache.set(key, Object.prototype.hasOwnProperty.call(preferences, key) ? preferences[key] : null);
      }
      for (const waiter of batch.waiters) {
        const slice = {};
        for (const key of waiter.keys) {
          if (Object.prototype.hasOwnProperty.call(preferences, key)) slice[key] = preferences[key];
        }
        waiter.resolve(slice);
      }
    })
    .catch((error) => {
      for (const waiter of batch.waiters) waiter.reject(error);
    });
}

/**
 * Read preferences from the server. Keys absent from the result are keys the
 * user has never set — the caller keeps its default (or promotes its mirrored
 * value; see useUserPreference).
 *
 * @param {string[]} keys
 * @returns {Promise<Object>} { [key]: value } for keys that HAVE a stored value
 */
export function readPreferences(keys) {
  const wanted = (keys || []).filter(Boolean);
  if (wanted.length === 0) return Promise.resolve({});

  return new Promise((resolve, reject) => {
    if (!readBatch) {
      readBatch = { keys: new Set(), waiters: [], timer: null };
      readBatch.timer = setTimeout(flushReads, READ_FLUSH_MS);
    }
    for (const key of wanted) readBatch.keys.add(key);
    readBatch.waiters.push({ keys: wanted, resolve, reject });
  });
}

// ── debounced writes ──────────────────────────────────────────────────

let writeBatch = null; // { entries: Map<key, value>, waiters: [{resolve, reject}], timer }

function flushWrites() {
  const batch = writeBatch;
  writeBatch = null;
  if (!batch) return;
  const entries = [...batch.entries].map(([key, value]) => ({ key, value }));

  call('set', { entries })
    .then(() => {
      const uid = currentUid();
      syncCacheOwner(uid);
      for (const { key, value } of entries) cache.set(key, value);
      for (const waiter of batch.waiters) waiter.resolve();
    })
    .catch((error) => {
      // The cache is NOT updated on failure, so the next read re-asks the
      // server rather than serving a value that never landed.
      for (const waiter of batch.waiters) waiter.reject(error);
    });
}

/**
 * Persist one preference. Coalesced with any other write in the same window;
 * a key written twice before the flush sends only the last value, which is the
 * correct reading of "the user dragged three times and then stopped".
 *
 * @returns {Promise<void>} rejects if the batch it joined failed to save
 */
export function writePreference(key, value) {
  return new Promise((resolve, reject) => {
    if (!writeBatch) {
      writeBatch = { entries: new Map(), waiters: [], timer: null };
      writeBatch.timer = setTimeout(flushWrites, WRITE_DEBOUNCE_MS);
    }
    writeBatch.entries.set(key, value);
    writeBatch.waiters.push({ resolve, reject });
  });
}

/** Reset `key` to its default everywhere — server row deleted, mirror dropped. */
export async function removePreference(key) {
  const uid = currentUid();
  syncCacheOwner(uid);
  cache.set(key, null);
  clearMirror(uid, key);
  await call('remove', { keys: [key] });
}

/** Test seam: run any queued read/write batch immediately. */
export function _flushForTests() {
  if (readBatch) { clearTimeout(readBatch.timer); flushReads(); }
  if (writeBatch) { clearTimeout(writeBatch.timer); flushWrites(); }
}

/**
 * Test seam: back to a cold module — cache cleared and any queued batch
 * DROPPED rather than flushed.
 *
 * The batches are module state that outlives a render tree, so without this a
 * test that mounts a component and finishes before the 0ms flush leaks its keys
 * into whichever test the timer lands in. Waiters are resolved emptily rather
 * than rejected: a dropped batch has no answer, and an unhandled rejection
 * would fail an unrelated test somewhere downstream.
 */
export function _resetForTests() {
  if (readBatch) {
    clearTimeout(readBatch.timer);
    for (const waiter of readBatch.waiters) waiter.resolve({});
    readBatch = null;
  }
  if (writeBatch) {
    clearTimeout(writeBatch.timer);
    for (const waiter of writeBatch.waiters) waiter.resolve();
    writeBatch = null;
  }
  resetCache();
}

export const _internals = { mirrorKey, currentUid, lastKnownUid, MIRROR_PREFIX, UID_BREADCRUMB, WRITE_DEBOUNCE_MS };
